import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  Modal,
  Search,
  SelectionBox,
  SelectionBoxGroup,
  Stepper,
} from '@enamelai/design-system'
import {
  ADD_MEMBER_AUTO_PAY_METHODS,
  ADD_MEMBER_DEFAULTS,
  ADD_MEMBER_FREQUENCIES,
  ADD_MEMBER_NOTIFY_OPTIONS,
  ADD_MEMBER_PATIENTS,
  ADD_MEMBER_PLAN_INCLUSIONS,
  ADD_MEMBER_STEPS,
  membershipNotifyEmailBody,
  UPDATE_PLAN_DEFAULTS,
  wantsMembershipWelcomeEmail,
} from '@constants/Membership/membershipPage.constants'
import { useNotificationToast } from '@context/NotificationToastContext'
import type { PatientWithInsurance } from '@dataTypes/appointment.type'
import type {
  MembershipAddMemberAvatarColor,
  MembershipAddMemberOption,
  MembershipAddMemberPatient,
  MembershipAddMemberPlanOption,
  MembershipMemberApiPaymentCycle,
  MembershipPageMember,
  MembershipTier,
  MembershipViewSource,
} from '@dataTypes/membership.type'
import {
  isEmailBackendEnabled,
  requestGoogleWorkspaceStatus,
} from '@services/api/email.api'
import {
  fetchPatientById,
  searchPatientsByName,
} from '@services/api/patient.api'
import { sendEmailMessage } from '@store/actions/email.action'
import {
  createMembershipMember,
  getAllMembershipTiers,
  updateMembershipMember,
} from '@store/actions/membership.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import {
  createFailedToast,
  createMemberPlanUpdatedToast,
  createNewMemberToast,
} from '@utils/notificationToast.util'
import './AddMemberModal.css'
import './UpdateMembershipPlanModal.css'

const LAST_STEP = ADD_MEMBER_STEPS.length - 1
const PATIENT_SEARCH_DELAY_MS = 300
const ADD_MEMBER_AVATAR_COLORS: MembershipAddMemberAvatarColor[] = [
  'red',
  'orange',
  'gray',
  'pink',
]

export type AddMemberModalMode = 'add' | 'update'

type AddMemberModalProps = {
  open: boolean
  onClose: () => void
  onAdded?: (member?: MembershipViewSource) => void
  mode?: AddMemberModalMode
  member?: MembershipPageMember | null
}

const toDateOnly = (value: Date): string => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const addMonths = (value: Date, months: number): Date => {
  const next = new Date(value)
  next.setMonth(next.getMonth() + months)
  return next
}

const expiryMonthsForCycle = (paymentCycle: string): number =>
  paymentCycle === 'annual' ? 12 : 1

const isPaymentCycle = (
  value: string,
): value is MembershipMemberApiPaymentCycle =>
  value === 'monthly' || value === 'annual'

const priceForCycle = (
  plan: MembershipAddMemberPlanOption,
  paymentCycle: string,
): number =>
  paymentCycle === 'annual'
    ? (plan.annualPrice ?? plan.monthlyPrice)
    : plan.monthlyPrice

const formatPlanPrice = (amount: number): string =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`

const toAddMemberPlan = (
  tier: MembershipTier,
): MembershipAddMemberPlanOption => {
  const priceLabel = formatPlanPrice(tier.monthlyPrice)

  return {
    value: tier.id,
    title: `${tier.name} membership - ${priceLabel}/mo`,
    shortTitle: `${tier.name} - ${priceLabel}/mo`,
    reviewLabel: tier.name,
    monthlyPrice: tier.monthlyPrice,
    annualPrice: tier.annualPrice,
    description: tier.description || `${tier.name} Tier`,
    inclusions: tier.benefits.map((benefit) => benefit.label),
    ...(tier.emailOnboardingMessage
      ? { emailOnboardingMessage: tier.emailOnboardingMessage }
      : {}),
  }
}

const NOTIFY_CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  'email-sms': 'Email & SMS',
}

const formatAutoPayLabel = (option: MembershipAddMemberOption): string => {
  const brand = (option.descriptionPrefix ?? 'Visa ending in ')
    .replace(/ ending in $/, '')
    .toLowerCase()

  return `${option.title} - ${brand} ending in ${option.lastFour}`
}

const paymentCycleValue = (label?: string): string => {
  const normalized = label?.trim().toLowerCase()
  if (normalized === 'monthly' || normalized === 'annual') return normalized
  return ADD_MEMBER_DEFAULTS.frequency
}

const toAddMemberPatient = (
  patient: PatientWithInsurance,
  index: number,
): MembershipAddMemberPatient => ({
  id: patient.patient_id,
  name: getFullName(patient, true),
  kind: 'patient',
  avatarColor:
    ADD_MEMBER_AVATAR_COLORS[index % ADD_MEMBER_AVATAR_COLORS.length],
  phone: patient.cellphone,
  email: patient.email,
})

const getOptionTitle = (
  options: Array<{ value: string; title: string }>,
  value: string,
): string => options.find((option) => option.value === value)?.title ?? value

const getFirstName = (name: string): string =>
  name.trim().split(/\s+/)[0] || name

const formatUsd = (amount: number): string => `$${amount.toFixed(2)}`

const formatEffectiveDate = (value: Date): string =>
  `Immediate, Today - ${new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(value)}`

const getAutoPayReviewValue = (option?: MembershipAddMemberOption): string => {
  if (!option) return '—'
  if (!option.lastFour) return option.title
  if (option.value === 'digital-bank') {
    return `${option.title} - Bank Accnt. ending in ${option.lastFour}`
  }
  return `${option.title} - visa ending in ${option.lastFour}`
}

const renderAccountDescription = (
  option: MembershipAddMemberOption,
): string | undefined =>
  option.lastFour
    ? `${option.descriptionPrefix ?? ''}${option.lastFour}`
    : option.description

const getUpdatePatient = (
  member?: MembershipPageMember | null,
  contact?: { email?: string; phone?: string } | null,
): MembershipAddMemberPatient | undefined => {
  if (!member) return undefined

  const match = ADD_MEMBER_PATIENTS.find(
    (patient) => patient.name === member.name && patient.kind === 'patient',
  )

  return {
    id: member.id,
    name: member.name,
    kind: 'patient',
    avatarColor: match?.avatarColor ?? 'red',
    phone: contact?.phone ?? match?.phone,
    email: contact?.email ?? match?.email,
  }
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  open,
  onClose,
  onAdded,
  mode = 'add',
  member,
}) => {
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const tiers = useAppSelector((state) => state.membership.tiers)
  const isUpdate = mode === 'update'
  const firstStep = isUpdate ? 1 : 0
  const billingDefaults = isUpdate ? UPDATE_PLAN_DEFAULTS : ADD_MEMBER_DEFAULTS
  const [step, setStep] = useState(firstStep)
  const [showPlanConfirm, setShowPlanConfirm] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPatientId, setSelectedPatientId] = useState('')
  const [frequency, setFrequency] = useState<string>(billingDefaults.frequency)
  const [autoPay, setAutoPay] = useState<string>(billingDefaults.autoPay)
  const [planId, setPlanId] = useState<string>(billingDefaults.planId)
  const [notify, setNotify] = useState<string>(billingDefaults.notify)
  const [saving, setSaving] = useState(false)
  const [googleMailboxConnected, setGoogleMailboxConnected] = useState(true)
  const savingRef = useRef(false)
  const [searchedPatients, setSearchedPatients] = useState<
    MembershipAddMemberPatient[]
  >([])
  const [selectedLivePatient, setSelectedLivePatient] =
    useState<MembershipAddMemberPatient | null>(null)
  const [updatePatientContact, setUpdatePatientContact] = useState<{
    email?: string
    phone?: string
  } | null>(null)

  useEffect(() => {
    if (!open) {
      setStep(0)
      setShowPlanConfirm(false)
      setSearchQuery('')
      setSelectedPatientId('')
      setSelectedLivePatient(null)
      setUpdatePatientContact(null)
      setSearchedPatients([])
      setSaving(false)
      setFrequency(ADD_MEMBER_DEFAULTS.frequency)
      setAutoPay(ADD_MEMBER_DEFAULTS.autoPay)
      setPlanId(ADD_MEMBER_DEFAULTS.planId)
      setNotify(ADD_MEMBER_DEFAULTS.notify)
      return
    }

    if (!isUpdate) return

    const updatePatient = getUpdatePatient(member)
    setStep(1)
    setSearchQuery('')
    setSelectedPatientId(updatePatient?.id ?? '')
    setFrequency(paymentCycleValue(member?.paymentCycle))
    setAutoPay(UPDATE_PLAN_DEFAULTS.autoPay)
    setPlanId(
      member?.membershipTierId || member?.tier || UPDATE_PLAN_DEFAULTS.planId,
    )
    setNotify(UPDATE_PLAN_DEFAULTS.notify)
  }, [isUpdate, member, open])

  useEffect(() => {
    if (!open) return
    void dispatch(getAllMembershipTiers())
  }, [dispatch, open])

  useEffect(() => {
    if (!open) return
    if (!isEmailBackendEnabled()) {
      setGoogleMailboxConnected(false)
      return
    }

    void requestGoogleWorkspaceStatus()
      .then((status) => {
        setGoogleMailboxConnected(Boolean(status?.isConnected))
      })
      .catch(() => {
        setGoogleMailboxConnected(false)
      })
  }, [open])

  useEffect(() => {
    if (!open || !isUpdate) {
      setUpdatePatientContact(null)
      return
    }

    const patientId = member?.patientId
    if (!patientId) return

    let cancelled = false
    void fetchPatientById(patientId, true)
      .then((response) => {
        if (cancelled) return
        const patient = response.data?.patient
        setUpdatePatientContact({
          email: patient?.email,
          phone: patient?.cellphone,
        })
      })
      .catch(() => {
        if (!cancelled) setUpdatePatientContact(null)
      })

    return () => {
      cancelled = true
    }
  }, [isUpdate, member?.patientId, open])

  useEffect(() => {
    if (!open || isUpdate) return

    const query = searchQuery.trim()
    if (!query) {
      setSearchedPatients([])
      return
    }

    let cancelled = false
    const timer = setTimeout(() => {
      void searchPatientsByName(query)
        .then((response) => {
          if (cancelled) return
          if (response.statusCode !== 200) {
            setSearchedPatients([])
            return
          }

          setSearchedPatients(
            (response.data?.patients ?? []).map((patient, index) =>
              toAddMemberPatient(patient, index),
            ),
          )
        })
        .catch(() => {
          if (!cancelled) setSearchedPatients([])
        })
    }, PATIENT_SEARCH_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [isUpdate, open, searchQuery])

  const filteredPatients = searchedPatients

  const planOptions = useMemo(() => {
    const enabledTiers = tiers.filter((tier) => tier.enabled)
    const currentTier = isUpdate
      ? tiers.find((tier) => tier.id === member?.membershipTierId)
      : undefined
    const visibleTiers =
      currentTier && !enabledTiers.some((tier) => tier.id === currentTier.id)
        ? [currentTier, ...enabledTiers]
        : enabledTiers

    return visibleTiers.map(toAddMemberPlan)
  }, [isUpdate, member?.membershipTierId, tiers])

  useEffect(() => {
    if (!open || planOptions.length === 0) {
      return
    }

    if (planOptions.some((plan) => plan.value === planId)) return

    if (isUpdate && member) {
      const currentPlan =
        planOptions.find((plan) => plan.value === member.membershipTierId) ??
        planOptions.find(
          (plan) => plan.reviewLabel.toLowerCase() === member.tier,
        )
      if (currentPlan) {
        setPlanId(currentPlan.value)
        return
      }
    }

    if (!isUpdate) {
      setPlanId(planOptions[0].value)
    }
  }, [isUpdate, member, open, planId, planOptions])

  const selectedPatient = isUpdate
    ? getUpdatePatient(member, updatePatientContact)
    : (searchedPatients.find((patient) => patient.id === selectedPatientId) ??
      selectedLivePatient)
  const selectedPlan = planOptions.find((plan) => plan.value === planId)
  const selectedAutoPay = ADD_MEMBER_AUTO_PAY_METHODS.find(
    (option) => option.value === autoPay,
  )
  const canContinue =
    !saving && (step === 0 ? Boolean(selectedPatientId) : Boolean(selectedPlan))
  const reviewAmount = formatUsd(
    selectedPlan ? priceForCycle(selectedPlan, frequency) : 0,
  )
  const reviewEffectiveDate = formatEffectiveDate(new Date())

  const handleClose = (): void => {
    onClose()
  }

  const sendMembershipWelcomeEmail = async (
    patientName: string,
    patientEmail: string | undefined,
    planName: string,
    inclusions: string[],
    template: string | undefined,
    savedAction: string,
  ): Promise<void> => {
    const email = patientEmail?.trim() ?? ''
    if (!email.includes('@')) {
      showToast(
        createFailedToast(
          'Email not sent',
          `${patientName} was ${savedAction}, but this patient has no email address.`,
        ),
      )
      return
    }

    const emailResult = await dispatch(
      sendEmailMessage({
        to: [email],
        subject: `Welcome to your ${planName} membership`,
        body: membershipNotifyEmailBody(
          patientName,
          planName,
          inclusions,
          template,
        ),
      }),
    )

    if (sendEmailMessage.rejected.match(emailResult)) {
      showToast(
        createFailedToast(
          'Email not sent',
          emailResult.payload ||
            `The member was ${savedAction}, but the welcome email could not be sent.`,
        ),
      )
    }
  }

  const handlePrimaryClick = (): void => {
    if (isUpdate && step === 1) {
      setShowPlanConfirm(true)
      return
    }

    if (step < LAST_STEP) {
      setStep((current) => current + 1)
      return
    }

    if (wantsMembershipWelcomeEmail(notify) && !googleMailboxConnected) {
      showToast(
        createFailedToast(
          'Google email',
          `Connect a Google mailbox under Settings > Communications > Email before ${
            isUpdate ? 'updating' : 'adding'
          } a member.`,
        ),
      )
      return
    }

    if (isUpdate) {
      if (
        !member ||
        !selectedPlan ||
        !isPaymentCycle(frequency) ||
        savingRef.current
      ) {
        return
      }

      const today = new Date()
      savingRef.current = true
      setSaving(true)
      void dispatch(
        updateMembershipMember({
          membershipMemberId: member.id,
          membershipTierId: selectedPlan.value,
          paymentCycle: frequency,
          expiryDate: toDateOnly(
            addMonths(today, expiryMonthsForCycle(frequency)),
          ),
          recurringAmount: priceForCycle(selectedPlan, frequency),
          notificationChannels: NOTIFY_CHANNEL_LABELS[notify],
        }),
      ).then(async (result) => {
        if (updateMembershipMember.rejected.match(result)) {
          savingRef.current = false
          setSaving(false)
          showToast(
            createFailedToast(
              'Unable to update member',
              result.payload || 'Failed to update membership plan.',
            ),
          )
          return
        }

        const planName = selectedPlan.reviewLabel ?? 'membership'
        const patientName = selectedPatient?.name ?? member.name
        showToast(createMemberPlanUpdatedToast(patientName))
        onAdded?.(result.payload)

        if (wantsMembershipWelcomeEmail(notify)) {
          await sendMembershipWelcomeEmail(
            patientName,
            selectedPatient?.email,
            planName,
            selectedPlan.inclusions,
            selectedPlan.emailOnboardingMessage,
            'updated',
          )
        }

        savingRef.current = false
        setSaving(false)
        handleClose()
      })
      return
    }

    if (
      !selectedPatient ||
      !selectedPlan ||
      !isPaymentCycle(frequency) ||
      savingRef.current
    ) {
      return
    }

    const today = new Date()
    savingRef.current = true
    setSaving(true)
    void dispatch(
      createMembershipMember({
        patientId: selectedPatient.id,
        membershipTierId: selectedPlan.value,
        paymentCycle: frequency,
        memberSince: toDateOnly(today),
        expiryDate: toDateOnly(
          addMonths(today, expiryMonthsForCycle(frequency)),
        ),
        recurringAmount: priceForCycle(selectedPlan, frequency),
        autoPayMethod: selectedAutoPay
          ? formatAutoPayLabel(selectedAutoPay)
          : undefined,
        notificationChannels: NOTIFY_CHANNEL_LABELS[notify],
      }),
    ).then(async (result) => {
      if (createMembershipMember.rejected.match(result)) {
        savingRef.current = false
        setSaving(false)
        showToast(
          createFailedToast(
            'Unable to add member',
            result.payload || 'Failed to add member.',
          ),
        )
        return
      }

      const planName = selectedPlan.reviewLabel ?? 'membership'
      showToast(createNewMemberToast(selectedPatient.name, planName))
      onAdded?.(result.payload)

      if (wantsMembershipWelcomeEmail(notify)) {
        await sendMembershipWelcomeEmail(
          selectedPatient.name,
          selectedPatient.email,
          planName,
          selectedPlan.inclusions,
          selectedPlan.emailOnboardingMessage,
          'added',
        )
      }

      savingRef.current = false
      setSaving(false)
      handleClose()
    })
  }

  const handleSecondaryClick = (): void => {
    if (step <= firstStep) {
      handleClose()
      return
    }

    setStep((current) => current - 1)
  }

  const handleConfirmPlan = (): void => {
    setShowPlanConfirm(false)
    setStep(2)
  }

  const handleCancelPlanConfirm = (): void => {
    setShowPlanConfirm(false)
  }

  const handleStepClick = (nextStep: number): void => {
    if (nextStep < firstStep || nextStep >= step) return

    setStep(nextStep)
  }

  const previewName = selectedPatient?.name ?? '[Patient name]'
  const previewFirstName = getFirstName(previewName)
  const previewPlan = selectedPlan?.reviewLabel ?? '[Plan name]'
  const previewEmail = selectedPatient?.email ?? '—'
  const previewPhone = selectedPatient?.phone ?? '—'
  const previewInclusions =
    selectedPlan?.inclusions ?? ADD_MEMBER_PLAN_INCLUSIONS
  const savedEmailTemplate = selectedPlan?.emailOnboardingMessage?.trim()
  const emailPreviewCopy = savedEmailTemplate
    ? membershipNotifyEmailBody(
        previewName,
        previewPlan,
        previewInclusions,
        savedEmailTemplate,
      )
    : null

  const emailPreview = (
    <div className="add-member-modal__message">
      <p className="add-member-modal__section-title">Email content:</p>
      <div className="add-member-modal__message-body">
        <p className="add-member-modal__message-meta">
          Sending to:{'  '}
          <span className="add-member-modal__message-accent">
            {previewEmail}
          </span>
        </p>
        {emailPreviewCopy ? (
          emailPreviewCopy.includes('<') ? (
            <div
              className="add-member-modal__message-copy"
              dangerouslySetInnerHTML={{ __html: emailPreviewCopy }}
            />
          ) : (
            <p className="add-member-modal__message-copy add-member-modal__message-copy--template">
              {emailPreviewCopy}
            </p>
          )
        ) : (
          <>
            <p className="add-member-modal__message-greeting">
              Hello {previewName},
            </p>
            <p className="add-member-modal__message-copy">
              Welcome to your{' '}
              <span className="add-member-modal__message-accent">
                {previewPlan}
              </span>{' '}
              membership! We are excited to have you as part of our dental
              family.
            </p>
            <div className="add-member-modal__inclusions">
              <p className="add-member-modal__inclusions-title">Inclusions:</p>
              <ul>
                {previewInclusions.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  )

  const smsPreview = (
    <div className="add-member-modal__message">
      <p className="add-member-modal__section-title">SMS preview:</p>
      <div className="add-member-modal__message-body">
        <p className="add-member-modal__message-meta">
          Sending to:{'  '}
          <span className="add-member-modal__message-accent">
            {previewPhone}
          </span>
        </p>
        <div className="add-member-modal__sms-copy">
          <p>
            Hi{' '}
            <span className="add-member-modal__message-accent">
              {previewFirstName}
            </span>
            , your{' '}
            <span className="add-member-modal__message-accent">
              {previewPlan}
            </span>
            {isUpdate ? ' is now ACTIVE!' : ' memberships is now ACTIVE!'}
          </p>
          <p className="add-member-modal__sms-break">Inclusions:</p>
          {previewInclusions.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </div>
      </div>
    </div>
  )

  const showEmailPreview = notify === 'email' || notify === 'email-sms'
  const showSmsPreview = notify === 'sms' || notify === 'email-sms'

  return (
    <>
      <Modal
        open={open && !showPlanConfirm}
        onClose={handleClose}
        size="md"
        title={isUpdate ? 'Update membership plan' : 'Add member'}
        className="add-member-modal"
        bodyClassName="add-member-modal__body"
        primaryLabel={
          step === LAST_STEP
            ? isUpdate
              ? 'Update member'
              : 'Add member'
            : 'Next'
        }
        primaryDisabled={!canContinue || saving}
        onPrimaryClick={handlePrimaryClick}
        secondaryLabel={isUpdate || step > 0 ? 'Previous' : 'Cancel'}
        onSecondaryClick={handleSecondaryClick}
        footerStart={
          isUpdate || step > 0 ? (
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
          ) : undefined
        }
      >
        <Stepper
          className="add-member-modal__stepper"
          steps={ADD_MEMBER_STEPS}
          currentStep={step}
          onStepClick={handleStepClick}
        />

        {step === 0 && !isUpdate ? (
          <div className="add-member-modal__step">
            <Search
              className="add-member-modal__search"
              value={searchQuery}
              placeholder="Search by name or phone number"
              ariaLabel="Search by name or phone number"
              onChange={setSearchQuery}
              onClear={() => setSearchQuery('')}
            />
            {filteredPatients.length === 0 ? (
              <p className="add-member-modal__empty">
                {!searchQuery.trim()
                  ? 'Search by patient name'
                  : 'No patients found'}
              </p>
            ) : (
              <div className="add-member-modal__patients" role="listbox">
                {filteredPatients.map((patient) => {
                  const selected = patient.id === selectedPatientId

                  return (
                    <button
                      key={patient.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`add-member-modal__patient${
                        selected ? ' add-member-modal__patient--selected' : ''
                      }`}
                      onClick={() => {
                        setSelectedPatientId(patient.id)
                        setSelectedLivePatient(patient)
                      }}
                    >
                      <Avatar
                        size={40}
                        color={patient.avatarColor}
                        name={
                          patient.kind === 'phone' ? undefined : patient.name
                        }
                        alt={patient.name}
                      />
                      <span className="add-member-modal__patient-name">
                        {patient.name}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        ) : null}

        {step === 1 ? (
          <div className="add-member-modal__step">
            <div className="add-member-modal__section">
              <p className="add-member-modal__section-title">
                Choose a billing frequency
              </p>
              <SelectionBoxGroup
                className="add-member-modal__option-group add-member-modal__option-group--fill"
                name="billing-frequency"
                value={frequency}
                onChange={setFrequency}
              >
                {ADD_MEMBER_FREQUENCIES.map((option) => (
                  <SelectionBox
                    key={option.value}
                    value={option.value}
                    title={option.title}
                    description={option.description}
                    showSelectIndicator={false}
                  />
                ))}
              </SelectionBoxGroup>
            </div>
            <div className="add-member-modal__section">
              <p className="add-member-modal__section-title">
                Choose a auto-pay method
              </p>
              <SelectionBoxGroup
                className="add-member-modal__option-group"
                name="auto-pay-method"
                value={autoPay}
                onChange={setAutoPay}
              >
                {ADD_MEMBER_AUTO_PAY_METHODS.map((option) => (
                  <SelectionBox
                    key={option.value}
                    value={option.value}
                    title={option.title}
                    description={renderAccountDescription(option)}
                    showSelectIndicator={false}
                  />
                ))}
              </SelectionBoxGroup>
            </div>
            <div className="add-member-modal__section">
              <p className="add-member-modal__section-title">
                Choose membership plan
              </p>
              {planOptions.length === 0 ? (
                <p className="add-member-modal__empty">
                  No membership plans found
                </p>
              ) : (
                <SelectionBoxGroup
                  className="add-member-modal__option-group add-member-modal__option-group--stack"
                  name="membership-plan"
                  value={planId}
                  onChange={setPlanId}
                >
                  {planOptions.map((option) => (
                    <SelectionBox
                      key={option.value}
                      value={option.value}
                      title={isUpdate ? option.shortTitle : option.title}
                      description={option.description}
                    />
                  ))}
                </SelectionBoxGroup>
              )}
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="add-member-modal__step">
            <div className="add-member-modal__section">
              <p className="add-member-modal__section-title">
                Notify patient via:
              </p>
              <SelectionBoxGroup
                className="add-member-modal__option-group"
                name="notify-patient"
                value={notify}
                onChange={setNotify}
              >
                {ADD_MEMBER_NOTIFY_OPTIONS.map((option) => (
                  <SelectionBox
                    key={option.value}
                    value={option.value}
                    title={option.title}
                    description={option.description}
                    showSelectIndicator={false}
                  />
                ))}
              </SelectionBoxGroup>
            </div>
            {showEmailPreview || showSmsPreview ? (
              <div className="add-member-modal__previews">
                {showEmailPreview ? emailPreview : null}
                {showSmsPreview ? smsPreview : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (
          <div className="add-member-modal__review">
            <div className="add-member-modal__review-card add-member-modal__review-card--patient">
              <div className="add-member-modal__review-patient">
                <Avatar
                  size={40}
                  color={selectedPatient?.avatarColor ?? 'gray'}
                  name={
                    selectedPatient?.kind === 'phone'
                      ? undefined
                      : selectedPatient?.name
                  }
                  alt={selectedPatient?.name ?? 'Patient'}
                />
                <div>
                  <p className="add-member-modal__review-name">
                    {selectedPatient?.name ?? '—'}
                  </p>
                  <p className="add-member-modal__review-meta">Patient</p>
                </div>
              </div>
              <div className="add-member-modal__review-plan">
                <p className="add-member-modal__review-value">
                  {selectedPlan?.title ?? '—'}
                </p>
                <p className="add-member-modal__review-meta">
                  {selectedPlan?.description ?? '—'}
                </p>
              </div>
            </div>
            <div className="add-member-modal__review-grid">
              <div className="add-member-modal__review-card">
                <p className="add-member-modal__review-value">
                  {reviewEffectiveDate}
                </p>
                <p className="add-member-modal__review-meta">Effective date</p>
              </div>
              <div className="add-member-modal__review-card">
                <p className="add-member-modal__review-value">{reviewAmount}</p>
                <p className="add-member-modal__review-meta">Initial amount</p>
              </div>
            </div>
            <div className="add-member-modal__review-grid">
              <div className="add-member-modal__review-card">
                <p className="add-member-modal__review-value">
                  {`${getOptionTitle(ADD_MEMBER_FREQUENCIES, frequency)} (${reviewAmount})`}
                </p>
                <p className="add-member-modal__review-meta">
                  Billing frequency
                </p>
              </div>
              <div className="add-member-modal__review-card">
                <p className="add-member-modal__review-value">
                  {getAutoPayReviewValue(selectedAutoPay)}
                </p>
                <p className="add-member-modal__review-meta">Auto-pay method</p>
              </div>
            </div>
            <div className="add-member-modal__review-card">
              <p className="add-member-modal__review-value">
                {getOptionTitle(ADD_MEMBER_NOTIFY_OPTIONS, notify)}
              </p>
              <p className="add-member-modal__review-meta">
                {showSmsPreview && !isUpdate
                  ? 'Notify via (SMS is a preview)'
                  : 'Notify via:'}
              </p>
            </div>
          </div>
        ) : null}
      </Modal>
      {isUpdate ? (
        <Modal
          open={open && showPlanConfirm}
          onClose={handleCancelPlanConfirm}
          size="sm"
          title="Update membership plan"
          className="update-plan-modal"
          bodyClassName="update-plan-modal__body"
          primaryLabel="Continue"
          secondaryLabel="Cancel"
          onPrimaryClick={handleConfirmPlan}
          onSecondaryClick={handleCancelPlanConfirm}
        >
          <p className="update-plan-modal__copy">
            Changes will be applied after the next billing cycle of the current
            membership plan.
            <br />
            <br />
            Would you like to continue?
          </p>
        </Modal>
      ) : null}
    </>
  )
}

export default AddMemberModal
